<script setup lang="ts">
import TextLink from '~/components/ui/text-link/TextLink.vue';
import type { Property, PropertyImage } from '~/core/entities/property';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapQuadrilateral from '~/core/service/googleMaps/components/GmapQuadrilateral/GmapQuadrilateral.vue';
import type { SectionName } from '~/types/mixpanel';
import FixedContactButton from '../FixedContactButton/FixedContactButton.vue';
import Modal from '../Modal/Modal.vue';
import PropertyPrices from '../Property/PropertyPrices/PropertyPrices.vue';
import type { SharingOption } from '../ShareContent';
import type { NavigationTab, Tab } from '../TabsNavigator';
import TabsNavigator from '../TabsNavigator/TabsNavigator.vue';

import { ArrowLeftIcon } from '@radix-icons/vue';
import { onClickOutside, useClipboard } from '@vueuse/core';
import { CopyToastDescription } from '~/components/CopyToastDescription/index';
import { useToast } from '~/components/ui/toast';
import { useEvents } from '~/composables/events/useEvents';
import { getQuadrilateral } from '~/core/service/googleMaps/pluscode';
import { cn, concatUrlPath, getPropertyImageAlt } from '~/utils';
import { grammarInflection } from '~~/utils';

import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';

const { copy } = useClipboard();
const { toast } = useToast();

const props = defineProps<{
	property: Property;
	launchYear: string;
}>();

const emit = defineEmits<{
	(e: 'click-photo', payload: { photoCount: number; photoUrl: string }): void;
	(e: 'click-video', payload: string): void;
	(e: 'click-fixed-contact-button'): void;
}>();

const isOpen = defineModel<boolean>();
const currentTab = defineModel<Tab>('tab', { default: 'images' });

function getColSpanClass(index: number): string {
	return (index + 1) % 3 === 0
		? 'col-span-2 h-[240px]'
		: 'col-span-1 h-[160px]';
}

const tabs = computed<NavigationTab[]>(
	() =>
		[
			{
				id: 'images',
				label: `${props.property.images.length} ${grammarInflection(props.property.images.length, 'foto', 'fotos')}`,
				active: currentTab.value === 'images'
			},
			props.property.videos?.length
				? {
						id: 'videos',
						label: `${props.property.videos.length} ${grammarInflection(props.property.videos.length, 'vídeo', 'vídeos')}`,
						active: currentTab.value === 'videos'
					}
				: null,
			{
				id: 'map',
				label: 'Mapa',
				active: currentTab.value === 'map'
			}
		].filter(Boolean) as NavigationTab[]
);

const isCurrentTab = (tab: Tab) => currentTab.value === tab;

const isCarouselOpen = ref<boolean>(false);
const currentCarouselItemIndex = ref<number>(0);

const sectionNames: Record<Tab, SectionName> = {
	images: 'photos modal',
	videos: 'videos modal',
	map: 'map modal'
};

const sectionName = computed<SectionName>(
	() => sectionNames[currentTab.value as Tab] || 'photos modal'
);

const imagesEl = ref<HTMLElement | null>(null);
const lastImagesScrollPosition = ref(0);
const { y: imagesScrollPosition } = useScroll(imagesEl);

const {
	public: { ORIGIN }
} = useRuntimeConfig();

const route = useRoute();

const { trackSharePropertyClicked } = useEvents();

const showShareContent = ref(false);

const shareUrl = computed<string>(() => concatUrlPath(ORIGIN, route.fullPath));

function toggleShareContent() {
	showShareContent.value = !showShareContent.value;
}

function shareMobile() {
	const data = {
		title: props.property.ad.title,
		url: shareUrl.value
	};
	if (navigator?.share && navigator?.canShare(data)) {
		navigator.share(data);
	} else {
		copy(shareUrl.value);
		toast({
			description: h(CopyToastDescription)
		});
	}
}

const quadrilateral = computed(() =>
	getQuadrilateral(props.property.pluscodeGroup, 1)
);

async function handleGoBack() {
	if (isCarouselOpen.value) {
		isCarouselOpen.value = false;

		await until(imagesEl).toBeTruthy();

		imagesScrollPosition.value = lastImagesScrollPosition.value;
		return;
	}

	isOpen.value = false;
}

function showCarousel(imageIndex: number) {
	lastImagesScrollPosition.value = imagesScrollPosition.value;
	currentCarouselItemIndex.value = imageIndex;
	isCarouselOpen.value = true;
}

watch(isOpen, () => {
	if (!isOpen.value) {
		isCarouselOpen.value = false;
		currentCarouselItemIndex.value = 0;
		currentTab.value = 'images';
	}
});

function handleShareClicked(option: SharingOption) {
	trackSharePropertyClicked({
		share_whatsapp: option.label === 'WhatsApp',
		share_facebook: option.label === 'Facebook',
		copy_link: false,
		share_email: option.label === 'Via e-mail',
		share_instagram: option.label === 'Instagram',
		share_twitter: option.label === 'Twitter'
	});
}

function handlePhotoClick(image: PropertyImage, index: number) {
	showCarousel(index);
	const photoUrl = image.watermarkUrl || image.url;

	if (!photoUrl) return;

	emit('click-photo', {
		photoUrl: photoUrl,
		photoCount: props.property.images.length
	});
}

function handleVideoClick(videoUrl: string) {
	emit('click-video', videoUrl);
}

const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);
onClickOutside(shareContentRef, () => (showShareContent.value = false), {
	ignore: [shareContentButton]
});

const isZoomed = ref(false);
const updateThumbnailZoom = (zoomed: boolean) => {
	isZoomed.value = zoomed;
};

const thumbnailCarouselBaseClasses = ['flex', 'flex-col'];
const thumbnailCarouselZoomedClasses = ['h-full', 'md:h-[calc(100%-208px)]'];
const thumbnailCarouselDefaultClasses = ['h-[calc(100%-208px)]'];

const thumbnailCarouselClasses = computed(() => {
	const heightClasses = isZoomed.value
		? thumbnailCarouselZoomedClasses
		: thumbnailCarouselDefaultClasses;

	return cn(...thumbnailCarouselBaseClasses, ...heightClasses);
});

const agentFirstName = computed(
	() => props.property.agent.name.split(' ')[0] || ''
);

const getImageAlt = (index: number | null) => {
	return getPropertyImageAlt(
		props.property.propertyType?.name,
		props.property.suites,
		props.property.region,
		props.property.city,
		props.property.askingPrice,
		index
	);
};
</script>

<template>
	<LazyClientOnly>
		<Modal
			v-model:open="isOpen"
			:show-close-icon="false"
			content-class="h-full max-w-screen max-h-screen flex flex-col bg-white"
			header-class="font-matter-sq text-xl md:text-2xl font-normal leading-[1.875rem] z-10 tracking-[0.02em] text-left text-primary justify-start items-start h-min pb-0 px-5 pt-5 md:p-5"
			footer-class="hidden"
			:is-property-details="true"
			prevent-interact-outside
		>
			<template #header>
				<Container class="!py-0 px-0">
					<DialogTitle
						:class="
							cn('flex size-full items-center md:pb-0', {
								'pb-5': isCarouselOpen
							})
						"
					>
						<div
							class="relative flex w-full items-start justify-start md:flex-nowrap md:items-center md:justify-center"
							:class="{ 'w-1/2 flex-wrap': !isCarouselOpen }"
						>
							<div
								:class="{
									'md:w-full': !isCarouselOpen
								}"
								class="order-1 flex w-1/2 items-center justify-between md:order-1 md:w-auto md:px-2"
							>
								<TextLink
									:element="'a'"
									class="flex items-center gap-2"
									@click="handleGoBack"
								>
									<ArrowLeftIcon class="size-5" />
									<span class="text-base font-normal">Voltar</span>
								</TextLink>
							</div>

							<div
								v-if="!isCarouselOpen"
								class="order-3 flex w-full md:order-2 md:justify-center"
							>
								<TabsNavigator
									v-model="currentTab"
									:tabs="tabs"
									class="flex gap-4"
									container-class="!justify-start md:!justify-center"
								/>
							</div>
							<div
								v-else
								:class="{
									'md:w-full': !isCarouselOpen,
									'w-full': isCarouselOpen
								}"
								class="order-2 flex grow justify-center px-10 font-matter-sq text-[18px] font-normal leading-[27px]"
							>
								{{ currentCarouselItemIndex + 1 }} de
								{{ property.images.length }}
								{{ grammarInflection(property.images.length, 'foto', 'fotos') }}
							</div>

							<div
								v-if="isCarouselOpen"
								class="order-3 block w-1/2 md:hidden"
							/>

							<div
								v-if="!isCarouselOpen"
								class="order-2 flex w-1/2 items-start justify-end md:order-3 md:w-full md:items-center"
							>
								<div class="flex flex-col items-start">
									<button
										ref="shareContentButton"
										class="z-10 hidden size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:flex"
										@click="toggleShareContent"
									>
										<IconMaterialSymbolsShare
											class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
										/>
									</button>

									<button
										class="z-10 flex size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:hidden"
										@click="shareMobile"
									>
										<IconMaterialSymbolsShare
											class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
										/>
									</button>
								</div>

								<div
									v-show="showShareContent"
									ref="shareContentRef"
									class="absolute -top-7 right-0 w-[125px] translate-y-[60%] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
								>
									<ShareContent
										v-model="showShareContent"
										variant="dropdown"
										mail-subject="Leia mais sobre este imóvel"
										:content-to-share="shareUrl"
										show-labels
										@share-clicked="handleShareClicked"
									/>
								</div>
							</div>
						</div>
					</DialogTitle>
				</Container>
			</template>
			<template #default>
				<Container
					v-if="!isCarouselOpen"
					class="flex grow py-5 md:pb-8 md:pt-6"
					:class="{
						'p-0 md:pb-8 md:pt-6': isCurrentTab('map'),
						'!px-0 md:!px-0': isCarouselOpen
					}"
				>
					<div class="flex h-full justify-between gap-8 overflow-y-scroll pb-8">
						<div
							class="size-full grow"
							:class="{
								'md:w-2/3': !isCarouselOpen,
								'md:w-full': isCarouselOpen
							}"
						>
							<div
								v-if="isCurrentTab('images') && !isCarouselOpen"
								ref="imagesEl"
								class="grid h-[calc(100dvh-150px)] grid-cols-2 gap-4 overflow-y-auto"
							>
								<NuxtImg
									v-for="(image, index) in property.images"
									:key="`property-image-${image.watermarkUrl || image.url}}}`"
									decoding="async"
									loading="lazy"
									provider="AWSServerlessImageHandler"
									:src="image.watermarkUrl || image.url"
									:placeholder="[50, 25]"
									:alt="getImageAlt(index)"
									:class="
										cn(
											getColSpanClass(index),
											'size-full cursor-pointer object-cover md:h-[320px]'
										)
									"
									@click="handlePhotoClick(image, index)"
								/>
							</div>
							<div
								v-if="isCurrentTab('videos')"
								class="flex h-[calc(100dvh-150px)] flex-col gap-5 overflow-y-auto md:gap-6"
							>
								<VideoPlayer
									v-for="video in property.videos"
									:key="`property-video-${video}`"
									:src="video"
									class="aspect-video flex-1"
									@play="handleVideoClick"
								/>
							</div>
							<div v-if="isCurrentTab('map')" class="h-full">
								<ClientOnly fallback-tag="div">
									<template #fallback>
										<div
											class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
										/>
									</template>

									<GmapMap
										:center="quadrilateral.centerCoordinates"
										:zoom="14"
										:min-zoom="14"
										class="size-full !bg-map-placeholder-image"
									>
										<GmapQuadrilateral
											:center-coordinates="null"
											:paths-coordinates="quadrilateral.pathsCoordinates"
											:count="1"
											:group-key="quadrilateral.groupKey"
											:is-selected="true"
											:is-clickable="false"
										/>
									</GmapMap>
								</ClientOnly>
							</div>
						</div>
						<div
							v-show="!isCarouselOpen"
							class="hidden h-fit w-1/3 min-w-[400px] md:block md:min-w-[350px]"
						>
							<PropertyPrices
								:asking-price="property.askingPrice"
								:condo-fee="property.condoFee"
								:condo-name="property.condoName"
								:tax="property.tax"
								:agent="property.agent"
								:company-name="property.company.name"
								:company-logo="property.company.smallLogo"
								:launch-year="launchYear"
								:property-type-name="property.propertyType?.name"
								:property-type="property.propertyType"
								:property-id="property.id"
								:commercial-id="property.commercialId"
								:region="property.region"
								:section-name="sectionName"
							/>
						</div>
					</div>
				</Container>
				<div v-else :class="thumbnailCarouselClasses">
					<ThumbnailCarousel
						v-model:selected-index="currentCarouselItemIndex"
						:images="property.images"
						:alt-callback="getImageAlt"
						@update-zoom="updateThumbnailZoom"
					/>
				</div>
				<FixedContactButton
					:show="!isCarouselOpen"
					:first-name="agentFirstName"
					@click="emit('click-fixed-contact-button')"
				/>
			</template>
		</Modal>
	</LazyClientOnly>
</template>
